import type { ReactNode } from 'react';
import { Fragment, useId } from 'react';
import { Link } from 'react-router';
import { LayoutGroup, motion } from 'motion/react';
import {
  Button,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Skeleton,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  FolderIcon,
  ListFilterIcon,
  Loader2Icon,
  PlusIcon,
  RefreshCwIcon,
} from 'lucide-react';
import { sharedElementTransition } from '@/shared/constants/motion.constants';
import type {
  NavigationPanelFilters,
  NavigationPanelItem,
  NavigationPanelProps,
} from './NavigationPanel.types';

/** Also the width a caller animates the panel to and from, so the two cannot drift. */
export const NAVIGATION_PANEL_WIDTH_PX = 300;

/** 20px per level clears the chevron column without pushing names off the panel. */
const INDENT_PX = 20;

const SKELETON_ROWS = [0, 1, 2, 3];

/**
 * A navigation panel: a plain card of rows, not a Tessra `Sidebar` — this app
 * re-points `--sidebar` to the canvas background, and `Sidebar` also registers
 * its own Cmd/Ctrl+B handler and a `sidebar_state` cookie write that would
 * clash with the app-shell rail already on the page.
 *
 * Rows either link (`hrefFor`) or report (`onSelectItem`), and nest one level
 * per depth where the caller supplies `children`, so a flat list and a folder
 * tree are the same component rather than two that drift.
 *
 * `p-xl` is load-bearing for focus-ring clearance on the first/last row of a
 * scrollable panel, not decoration — do not reduce it.
 */
export function NavigationPanel({
  title,
  items,
  activeItemId,
  isLoading,
  emptyMessage,
  error,
  createLabel,
  createTooltip = createLabel,
  canCreate,
  onCreate,
  hrefFor,
  onSelectItem,
  filters,
  footer,
  'data-testid': dataTestId,
}: NavigationPanelProps) {
  // Two panels on one page each animate their own selection; a shared id would
  // make the indicator fly between them.
  const selectionLayoutId = useId();

  const renderItem = (item: NavigationPanelItem, depth: number): ReactNode => (
    <PanelRow
      key={item.id}
      item={item}
      depth={depth}
      isActive={item.id === activeItemId}
      href={hrefFor?.(item.id)}
      onSelect={onSelectItem && (() => onSelectItem(item.id))}
      selectionLayoutId={selectionLayoutId}
      dataTestId={dataTestId}
    >
      {item.children?.length && item.branch?.isExpanded !== false ? (
        <ul className="flex flex-col gap-3xs">
          {item.children.map((child) => renderItem(child, depth + 1))}
        </ul>
      ) : null}
    </PanelRow>
  );

  return (
    <nav
      aria-label={title}
      data-testid={dataTestId}
      style={{ width: `${String(NAVIGATION_PANEL_WIDTH_PX)}px` }}
      className="flex shrink-0 flex-col gap-md overflow-y-auto rounded-lg bg-card p-xl"
    >
      <div
        data-testid={dataTestId && `${dataTestId}-header`}
        className="flex items-center justify-between gap-sm"
      >
        <span className="type-body-md-strong text-foreground">{title}</span>
        <div className="flex items-center gap-3xs">
          {filters && <FiltersMenu filters={filters} dataTestId={dataTestId} />}
          {createLabel !== undefined && (
            <TooltipProvider>
              <Tooltip>
                {/* The span keeps the trigger hoverable while the button is disabled. */}
                <TooltipTrigger asChild>
                  <span className="inline-flex">
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label={createLabel}
                      disabled={!canCreate}
                      onClick={onCreate}
                    >
                      <PlusIcon className="size-4" />
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>{createTooltip}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      </div>

      {isLoading ? (
        // The same height and gap as the rows they stand in for, so the list
        // does not shift as it resolves.
        <div data-testid={dataTestId && `${dataTestId}-skeleton`} className="flex flex-col gap-3xs">
          {SKELETON_ROWS.map((row) => (
            <Skeleton key={row} className="h-9 w-full rounded-lg" />
          ))}
        </div>
      ) : error ? (
        <div role="alert" className="flex flex-col items-start gap-xs px-sm">
          <p className="text-body-md text-foreground">{error.title}</p>
          {error.description && (
            <p className="text-body-sm text-muted-foreground">{error.description}</p>
          )}
          <Button variant="outline" size="sm" onClick={error.onRetry}>
            <RefreshCwIcon className="size-4" />
            {error.retryLabel}
          </Button>
        </div>
      ) : items.length === 0 ? (
        <div className="text-body-sm text-muted-foreground">{emptyMessage}</div>
      ) : (
        <LayoutGroup id={selectionLayoutId}>
          <ul className="flex flex-col gap-3xs">{items.map((item) => renderItem(item, 0))}</ul>
        </LayoutGroup>
      )}

      {footer}
    </nav>
  );
}

NavigationPanel.displayName = 'NavigationPanel';

interface FiltersMenuProps {
  filters: NavigationPanelFilters;
  dataTestId?: string;
}

function FiltersMenu({ filters, dataTestId }: FiltersMenuProps) {
  return (
    <DropdownMenu>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={filters.label}
                data-testid={dataTestId && `${dataTestId}-filters`}
              >
                <ListFilterIcon className="size-4" />
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent>{filters.label}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <DropdownMenuContent align="end">
        {filters.groups.map((group, index) => (
          <Fragment key={group.label}>
            {/* A rule between groups, never above the first: the menu opens on a
                label, not on a line. */}
            {index > 0 && <DropdownMenuSeparator />}
            <DropdownMenuLabel>{group.label}</DropdownMenuLabel>
            {/* One option per group is in force, and picking any of them is what
                puts the next one there, so an option already checked reports
                itself again rather than clearing the group. */}
            <DropdownMenuGroup>
              {group.options.map((option) => (
                <DropdownMenuCheckboxItem
                  key={option.id}
                  checked={option.id === group.value}
                  onCheckedChange={() => group.onChange(option.id)}
                >
                  {option.label}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuGroup>
          </Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

interface PanelRowProps {
  item: NavigationPanelItem;
  depth: number;
  isActive: boolean;
  href?: string;
  onSelect?: () => void;
  selectionLayoutId: string;
  dataTestId?: string;
  /** The rows nested under this one, already wrapped in their own list. */
  children?: ReactNode;
}

function PanelRow({
  item,
  depth,
  isActive,
  href,
  onSelect,
  selectionLayoutId,
  dataTestId,
  children,
}: PanelRowProps) {
  const { branch } = item;

  // Only the open row is picked out. Whether a row holds others is said by its
  // chevron, not by its weight.
  //
  // `sidebar-foreground` rather than the general subtle step: this is a
  // navigation surface, and that is the token the design system binds to one.
  const rowClasses = [
    'relative flex min-w-0 cursor-pointer items-center gap-xs rounded-lg py-xs pr-sm',
    'text-left text-body-md transition-colors duration-fast ease-out',
    isActive ? 'font-medium text-foreground' : 'text-sidebar-foreground hover:bg-surface',
  ].join(' ');

  // Indent the row's own content rather than nesting padded lists, so every
  // row's background spans the full panel width however deep it sits. Added to
  // the base step, not in place of it: depth 0 keeps the same inset as the
  // right edge.
  const rowStyle = { paddingLeft: `calc(var(--space-sm) + ${String(depth * INDENT_PX)}px)` };

  // A branch fetched on first open used to sit on an unchanged chevron, which
  // read as the click having missed.
  const ChevronIcon = branch?.isLoading
    ? Loader2Icon
    : branch?.isExpanded
      ? ChevronDownIcon
      : ChevronRightIcon;

  const content = (
    <>
      {isActive && (
        <motion.span
          layoutId={`${selectionLayoutId}-selection`}
          transition={sharedElementTransition.headerSpring}
          className="absolute inset-0 rounded-lg bg-accent"
          aria-hidden
        />
      )}

      {/* Positioned so they paint over the indicator, which is itself
          positioned and would otherwise cover them. The chevron keeps the
          glyph's own footprint in the row and takes its hit target from a
          pseudo-element, so the icon beside it stays on the same line without a
          slot sized for a button. */}
      {branch ? (
        <button
          type="button"
          onClick={(event) => {
            // A row that navigates would otherwise follow its link on the way
            // out, taking the user off the page they opened the branch from.
            event.preventDefault();
            event.stopPropagation();
            branch.onToggle();
          }}
          aria-expanded={branch.isExpanded}
          aria-busy={branch.isLoading}
          aria-label={branch.toggleLabel}
          data-testid={dataTestId && `${dataTestId}-toggle-${item.id}`}
          className="relative shrink-0 cursor-pointer text-muted-foreground after:absolute after:-inset-1.5 after:content-['']"
        >
          <ChevronIcon className={`size-4 ${branch.isLoading ? 'animate-spin' : ''}`} />
        </button>
      ) : (
        <span
          className="relative flex shrink-0 items-center text-muted-foreground"
          data-testid={dataTestId && `${dataTestId}-item-icon`}
        >
          {item.icon ?? <FolderIcon className="size-4" />}
        </span>
      )}

      <span className="relative min-w-0 flex-1 truncate">{item.label}</span>
      {/* `empty:hidden` so a trailing slot that renders nothing costs no gap:
          the badge here decides for itself whether it has anything to say. */}
      <span className="relative flex shrink-0 items-center empty:hidden">{item.trailing}</span>
    </>
  );

  const rowTestId = dataTestId && `${dataTestId}-item-${item.id}`;

  return (
    <li className="flex flex-col gap-3xs">
      {href !== undefined ? (
        <Link
          to={href}
          aria-current={isActive ? 'page' : undefined}
          data-testid={rowTestId}
          style={rowStyle}
          className={rowClasses}
        >
          {content}
        </Link>
      ) : (
        <div
          role="button"
          tabIndex={0}
          onClick={onSelect}
          onKeyDown={(event) => {
            // Keys aimed at the chevron are its own; acting here cancels the
            // activation click the browser would synthesise for it, so a branch
            // could never be collapsed from the keyboard.
            if (event.target !== event.currentTarget) return;
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            onSelect?.();
          }}
          aria-current={isActive ? 'page' : undefined}
          data-testid={rowTestId}
          style={rowStyle}
          className={rowClasses}
        >
          {content}
        </div>
      )}
      {children}
    </li>
  );
}
